export abstract class AbstractCacheService {
    readonly prefix = 'PI-';

    constructor(private storage: Storage) {}

    get length() {
        return this.storage.length;
    }

    getItem<T>(tag: string): T {
        const itemRetrieved = JSON.parse(
            this.storage.getItem(this.convertTag(tag))
        );
        // note that this takes advantage of 0 being false
        if (
            itemRetrieved?.expiration &&
            itemRetrieved.expiration < Date.now()
        ) {
            this.storage.removeItem(tag);
            return undefined;
        }
        return itemRetrieved?.value;
    }

    setItem<T>(
        tag: string,
        value: T,
        lifeSpanInMillis = 0, // zero means no expiration
        firstTime = true
    ) {
        try {
            return this.storage.setItem(
                this.convertTag(tag),
                JSON.stringify({
                    value: value,
                    expiration:
                        lifeSpanInMillis > 0
                            ? Date.now() + lifeSpanInMillis
                            : Number.MAX_SAFE_INTEGER
                })
            );
        } catch (e) {
            if (this.isQuotaExceeded(e) && firstTime) {
                this.doCleanupPass();
                this.setItem(tag, value, lifeSpanInMillis, false);
            }
        }
    }

    removeItem(tag: string) {
        return this.storage.removeItem(this.convertTag(tag));
    }

    key(index: number): string {
        return this.storage.key(index);
    }

    clearAll() {
        this.storage.clear();
    }

    /* I toyed with the idea of more elaborate cache cleaning mechanisms, but simple
       mechanism were haunted be edge cases. I decided to go with a simple approach
       with this being a place where future devs could refine. */
    private doCleanupPass() {
        const currentTimestamp = Date.now();
        for (let i = 0; i < this.storage.length; i++) {
            const tag = this.storage.key(i);
            const item = JSON.parse(this.storage.getItem(tag));
            if (item?.expiration && item.expiration < currentTimestamp) {
                this.storage.removeItem(tag);
            }
        }
    }

    private convertTag(tag: string) {
        return `${this.prefix}${tag}`;
    }

    // Following taken from: http://crocodillon.com/blog/always-catch-localstorage-security-and-quota-exceeded-errors
    private isQuotaExceeded(e: any) {
        let quotaExceeded = false;
        if (e) {
            if (e.code) {
                switch (e.code) {
                    case 22:
                        quotaExceeded = true;
                        break;
                    case 1014:
                        // Firefox
                        if (e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
                            quotaExceeded = true;
                        }
                        break;
                }
            } else if (e.number === -2147024882) {
                // Internet Explorer 8
                quotaExceeded = true;
            }
        }
        return quotaExceeded;
    }
}
